import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Colors } from '../utils/colors';
import { ebookDownloadService } from '../services/ebookDownloadService';
import { getEbookPdfInfo } from '../utils/ebookPdfMapping';
import { updateDownloadStats, loadAuthorizedDownloads } from '../redux/slices/ebookDownloadSlice';
import { selectIsAuthenticated } from '../redux/slices/authSlice';
import { PRODUCTS_IMAGES } from '../data/checkoutImages';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.03); }
`;

/* ─── Wrapper ────────────────────────────────────────────────────────────── */
const Wrapper = styled.div`
  width: 100%;
  max-width: 860px;
  margin: 0 auto;
`;

/* ─── Stats bar ──────────────────────────────────────────────────────────── */
const StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 28px;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  & .value {
    color: ${Colors.yellow};
    font-size: 22px;
    font-weight: 800;
    line-height: 1;
  }

  & .label {
    color: rgba(255, 255, 255, 0.35);
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.8px;
  }
`;

const StatDivider = styled.div`
  width: 1px;
  height: 32px;
  background: rgba(255, 255, 255, 0.08);
`;

const RefreshBtn = styled.button`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.18s, color 0.18s, background 0.18s;

  & .spin-icon {
    font-size: 13px;
    display: inline-block;
    ${(p) => p.$loading && css`animation: ${spin} 0.7s linear infinite;`}
  }

  &:hover {
    border-color: rgba(255, 215, 0, 0.3);
    color: ${Colors.yellow};
    background: rgba(255, 215, 0, 0.05);
  }

  &:focus-visible { outline: 2px solid ${Colors.yellow}; outline-offset: 2px; }
`;

/* ─── Mensagem de status ─────────────────────────────────────────────────── */
const Toast = styled.div`
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 20px;
  border-left: 3px solid ${(p) => (p.type === 'success' ? '#22c55e' : p.type === 'error' ? '#ef4444' : Colors.yellow)};
  background: ${(p) => (p.type === 'success' ? 'rgba(34,197,94,0.08)' : p.type === 'error' ? 'rgba(239,68,68,0.08)' : 'rgba(255,215,0,0.06)')};
  color: ${(p) => (p.type === 'success' ? '#4ade80' : p.type === 'error' ? '#f87171' : Colors.yellow)};
`;

/* ─── Grid de cards ──────────────────────────────────────────────────────── */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 16px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: border-color 0.2s, transform 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: rgba(255, 215, 0, 0.25);
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.4);
  }

  &.expired {
    opacity: 0.45;
    pointer-events: none;
  }
`;

const CoverWrap = styled.div`
  width: 100%;
  aspect-ratio: 3 / 4;
  background: rgba(255, 255, 255, 0.04);
  overflow: hidden;
  position: relative;
`;

const CoverImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;

  ${Card}:hover & { transform: scale(1.04); }
`;

const CoverFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  background: linear-gradient(135deg, rgba(255,215,0,0.1) 0%, rgba(255,215,0,0.03) 100%);
`;

const ExpiryBadge = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(4px);
  color: rgba(255, 255, 255, 0.55);
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const CardBody = styled.div`
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 10px;
`;

const CardTitle = styled.h3`
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  font-weight: 600;
  margin: 0;
  line-height: 1.4;
`;

const DownloadBtn = styled.button`
  width: 100%;
  padding: 9px 0;
  background: ${(p) => (p.disabled ? 'rgba(255,255,255,0.06)' : Colors.yellow)};
  color: ${(p) => (p.disabled ? 'rgba(255,255,255,0.25)' : '#111')};
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.18s, transform 0.18s, box-shadow 0.18s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: auto;

  &:not(:disabled):hover {
    background: #ffe54e;
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(255, 215, 0, 0.3);
    animation: ${pulse} 0.4s ease;
  }

  &:focus-visible { outline: 2px solid ${Colors.yellow}; outline-offset: 2px; }
`;

/* ─── Empty state ────────────────────────────────────────────────────────── */
const EmptyState = styled.div`
  text-align: center;
  padding: 56px 24px;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 16px;

  & .emoji { font-size: 48px; margin-bottom: 16px; }
  & h3 { color: rgba(255,255,255,0.6); font-size: 16px; font-weight: 600; margin: 0 0 8px; }
  & p  { color: rgba(255,255,255,0.3); font-size: 13px; margin: 0; }
`;

/* ═══════════════════════════════════════════════════════════════════════════ */

export default function EbookDownloadManager() {
  const dispatch = useDispatch();
  const { authorizedDownloads, downloadStats } = useSelector((state) => state.ebookDownload);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const purchasedEbooks = useSelector((state) => state.ebookAccess?.purchasedEbooks || []);

  const [loading,      setLoading]      = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [message,      setMessage]      = useState(null);

  const refreshDownloads = useCallback(() => {
    setLoading(true);
    try {
      dispatch(loadAuthorizedDownloads());
      dispatch(updateDownloadStats());
    } finally {
      setTimeout(() => setLoading(false), 400);
    }
  }, [dispatch]);

  // Sincroniza compras da API → localStorage quando autenticado
  useEffect(() => {
    if (!isAuthenticated || purchasedEbooks.length === 0) return;
    purchasedEbooks.forEach((ebook) => {
      ebookDownloadService.authorizeDownload(
        ebook.ebookId,
        ebook.titulo || ebook.ebookId,
        { method: ebook.paymentMethod, transactionId: ebook.transactionId }
      );
    });
    dispatch(loadAuthorizedDownloads());
    dispatch(updateDownloadStats());
  }, [isAuthenticated, purchasedEbooks, dispatch]);

  useEffect(() => {
    refreshDownloads();
  }, [refreshDownloads]);

  const handleDownload = (ebookId, ebookTitle) => {
    setDownloadingId(ebookId);
    try {
      const result = ebookDownloadService.downloadEbook(ebookId, ebookTitle);
      if (result.success) {
        setMessage({ type: 'success', text: `Download iniciado: ${ebookTitle}` });
        setTimeout(() => { dispatch(updateDownloadStats()); setMessage(null); }, 3000);
      } else {
        setMessage({ type: 'error', text: result.message });
        setTimeout(() => setMessage(null), 5000);
      }
    } catch (err) {
      setMessage({ type: 'error', text: `Erro: ${err.message}` });
      setTimeout(() => setMessage(null), 5000);
    }
    setDownloadingId(null);
  };

  const isExpired = (expiresAt) => new Date(expiresAt) < new Date();

  return (
    <Wrapper>
      {/* Stats + refresh */}
      <StatsRow>
        <Stat>
          <span className="value">{authorizedDownloads.length}</span>
          <span className="label">Disponíveis</span>
        </Stat>
        <StatDivider />
        <Stat>
          <span className="value">{downloadStats.totalDownloads}</span>
          <span className="label">Downloads</span>
        </Stat>

        <RefreshBtn onClick={refreshDownloads} $loading={loading} disabled={loading}>
          <span className="spin-icon">↻</span>
          Atualizar
        </RefreshBtn>
      </StatsRow>

      {message && <Toast type={message.type}>{message.text}</Toast>}

      {/* Grid de eBooks */}
      {authorizedDownloads.length === 0 ? (
        <EmptyState>
          <div className="emoji">📚</div>
          <h3>Nenhum eBook disponível</h3>
          <p>Realize uma compra para liberar seus downloads aqui.</p>
        </EmptyState>
      ) : (
        <Grid>
          {authorizedDownloads.map((ebook) => {
            const pdfInfo = getEbookPdfInfo(ebook.ebookId);
            const cover   = PRODUCTS_IMAGES[ebook.ebookId];
            const expired = isExpired(ebook.expiresAt);
            const isDownloading = downloadingId === ebook.ebookId;
            const title = pdfInfo?.title || ebook.ebookTitle;

            return (
              <Card key={ebook.ebookId} className={expired ? 'expired' : ''}>
                <CoverWrap>
                  {cover ? (
                    <CoverImg
                      src={cover}
                      alt={title}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <CoverFallback>📖</CoverFallback>
                  )}
                  <ExpiryBadge>
                    Até {new Date(ebook.expiresAt).toLocaleDateString('pt-BR')}
                  </ExpiryBadge>
                </CoverWrap>

                <CardBody>
                  <CardTitle>{title}</CardTitle>
                  <DownloadBtn
                    disabled={expired || isDownloading}
                    onClick={() => handleDownload(ebook.ebookId, title)}
                  >
                    {isDownloading ? (
                      <><span style={{ display:'inline-block', animation:`${spin} 0.7s linear infinite` }}>↻</span> Baixando…</>
                    ) : expired ? (
                      'Expirado'
                    ) : (
                      <>↓ Baixar PDF</>
                    )}
                  </DownloadBtn>
                </CardBody>
              </Card>
            );
          })}
        </Grid>
      )}
    </Wrapper>
  );
}
